import { useMemo } from 'react';
import { useQuery } from 'urql';
import { format, parseISO, differenceInDays, formatISO } from 'date-fns';
import { getSocialAnalyticsTimeSeries } from '../graphql';
import { GraphQLQueryVariables } from '../types';
import type {
  TimeSeriesData,
  TimeSeriesDataPoint,
  UseTimeSeriesDataReturn,
  MetricKey,
} from '../types/time-series.types';

// Import the constant for use in the hook
const networkChartNames = {
  INSTAGRAM: 'Instagram',
  instagram: 'Instagram',
  TIKTOK: 'TikTok',
  tiktok: 'TikTok',
  YOUTUBE: 'YouTube',
  youtube: 'YouTube',
  PINTEREST: 'Pinterest',
  pinterest: 'Pinterest',
} as const;

export const useTimeSeriesData = (variables: GraphQLQueryVariables): UseTimeSeriesDataReturn => {
  const shouldPause = !variables.clientId || !variables.startDate || !variables.endDate;

  const [{ data, fetching, error }] = useQuery({
    query: getSocialAnalyticsTimeSeries,
    variables: {
      clientId: variables.clientId || '',
      startDate: variables.startDate || '',
      endDate: variables.endDate || '',
      postTypes: variables.postTypes || [],
      includeEstimates: variables.includeEstimates || false,
      networks: variables.networks || [],
      projectIds: variables.projectIds,
      groupIds: variables.groupIds,
      segments: variables.segments,
      memberIds: variables.memberIds,
    },
    pause: shouldPause,
  });

  // Transform real time series data for charts with proper type safety
  const transformedData: TimeSeriesData = useMemo(() => {
    const socialAnalytics = data?.client?.socialAnalytics;

    if (!socialAnalytics) {
      return { reach: [], impressions: [], engagement: [], accountFollowersBreakdown: undefined };
    }

    const publishedPostBreakdown = socialAnalytics.publishedPostBreakdown;
    const networks = socialAnalytics.networks;

    // Validate that we have the required data structure
    if (!publishedPostBreakdown?.breakdown || !Array.isArray(publishedPostBreakdown.breakdown) ||
        !networks || !Array.isArray(networks)) {
      return { reach: [], impressions: [], engagement: [], accountFollowersBreakdown: undefined };
    }

    // Create network totals map for distribution - with proper type safety
    const networkTotals: Record<string, { reach: number; impressions: number; engagements: number }> = {};

    networks.forEach((network) => {
      if (network?.network) {
        networkTotals[network.network] = {
          reach: network.reach || 0,
          impressions: network.impressions || 0,
          engagements: network.engagements || 0,
        };
      }
    });

    // Group post breakdown by date and calculate post distribution
    const postsByDate: Record<string, { total: number; networks: Record<string, number> }> = {};

    publishedPostBreakdown.breakdown.forEach((item) => {
      if (!item?.date || !item?.network || typeof item.count !== 'number') {
        return; // Skip invalid items
      }

      const dateKey = item.date.toString();
      if (!postsByDate[dateKey]) {
        postsByDate[dateKey] = { total: 0, networks: {} };
      }
      postsByDate[dateKey].total += item.count;
      postsByDate[dateKey].networks[item.network] = (postsByDate[dateKey].networks[item.network] || 0) + item.count;
    });

    // Precompute total posts per network
    const networkPostTotals: Record<string, number> = {};
    publishedPostBreakdown.breakdown.forEach((item) => {
      if (!item?.network || typeof item.count !== 'number') {
        return;
      }
      networkPostTotals[item.network] = (networkPostTotals[item.network] || 0) + item.count;
    });

    // Get all unique dates and sort them
    const allDates = Object.keys(postsByDate).sort();

    if (allDates.length === 0) {
      return { reach: [], impressions: [], engagement: [], accountFollowersBreakdown: undefined };
    }

    // Smart date formatting based on date range
    const getDateLabel = (dateKey: string, allDates: string[]): string => {
      if (allDates.length === 0) return format(parseISO(dateKey), 'MMM');

      const startDate = parseISO(allDates[0]);
      const endDate = parseISO(allDates[allDates.length - 1]);
      const daysDiff = differenceInDays(endDate, startDate);

      // If range is less than 31 days, show day and month
      if (daysDiff < 31) {
        return format(parseISO(dateKey), 'MMM d');
      }

      // If range is less than 90 days, show month and day
      if (daysDiff < 90) {
        return format(parseISO(dateKey), 'MMM d');
      }

      // For longer ranges, just show month
      return format(parseISO(dateKey), 'MMM');
    };

    // Create time series data for each metric with proper validation
    const createTimeSeries = (metricKey: MetricKey): TimeSeriesDataPoint[] => {
      // Map 'engagement' to 'engagements' for networkTotals lookup
      const networkTotalsKey = metricKey === 'engagement' ? 'engagements' : metricKey;
      return allDates.map((dateKey) => {
        const dateData = postsByDate[dateKey];
        const dataPoint: TimeSeriesDataPoint = {
          month: getDateLabel(dateKey, allDates),
          Instagram: 0,
          TikTok: 0,
          YouTube: 0,
          Pinterest: 0,
        };

        // For each network, distribute its total metric based on post activity on this date
        Object.keys(networkTotals).forEach((networkName) => {
          const networkTotal = networkTotals[networkName]?.[networkTotalsKey] || 0;
          const networkPostsOnDate = dateData.networks[networkName] || 0;
          const totalNetworkPosts = networkPostTotals[networkName] || 0;

          // Distribute network total proportionally based on posts published on this date
          const networkValue = totalNetworkPosts > 0
            ? Math.round(networkTotal * (networkPostsOnDate / totalNetworkPosts))
            : 0;

          // Map network names to chart-friendly names with validation
          const chartNetworkName = networkChartNames[networkName as keyof typeof networkChartNames];

          if (chartNetworkName) {
            switch (chartNetworkName) {
              case 'Instagram':
                dataPoint.Instagram = networkValue;
                break;
              case 'TikTok':
                dataPoint.TikTok = networkValue;
                break;
              case 'YouTube':
                dataPoint.YouTube = networkValue;
                break;
              case 'Pinterest':
                dataPoint.Pinterest = networkValue;
                break;
            }
          }
        });

        return dataPoint;
      });
    };

    // Transform account followers breakdown to ensure date is always a string
    const processedAccountFollowersBreakdown = socialAnalytics.accountFollowersBreakdown
      ? {
          ...socialAnalytics.accountFollowersBreakdown,
          daily: {
            ...socialAnalytics.accountFollowersBreakdown.daily,
            followersByDate:
              socialAnalytics.accountFollowersBreakdown.daily?.followersByDate?.map(({ date, count }) => ({
                date: typeof date === 'string' ? date : formatISO(date as Date, { representation: 'date' }),
                count,
              })) || [],
          },
        }
      : undefined;

    try {
      const result = {
        reach: createTimeSeries('reach'),
        impressions: createTimeSeries('impressions'),
        engagement: createTimeSeries('engagement'),
        accountFollowersBreakdown: processedAccountFollowersBreakdown,
      };

      return result;
    } catch (error) {
      console.error('Error creating time series data:', error);
      return { reach: [], impressions: [], engagement: [], accountFollowersBreakdown: undefined };
    }
  }, [data]);

  return {
    data: transformedData,
    fetching,
    error,
  };
};
